import React, { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Dialog, useTheme } from '@mui/material';
import {
  onCheckoutPageView,
  onCheckoutPageClick,
  pushHistoryWithSearchParams,
  CHECKOUT_PAGE_CLICK_EVENTS,
  onCheckoutPageViewV2,
} from 'components/pages/checkout/utilities';
import Reviewed from 'components/pages/checkout/finance_app/reviewed';
import Declined from 'components/pages/checkout/finance_app/declined';
import Pending from 'components/pages/checkout/finance_app/pending';
import LoadingDecision from 'components/pages/checkout/finance_app/loading_decision';
import ApplicationForm from 'components/common/finance_app/application_form';
import PageLoader from 'components/common/page_loader';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useOrderDetails } from 'components/context/order_details';
import { useUserJourneys } from 'components/context/user_journeys';
import CapitalShopClient from 'clients/capital_shop';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import { FrustrationSignal, UserJourneyName } from 'constants/user_journeys';

const FinanceApp = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();

  const { orderRefresh } = useCheckoutFlow();
  const { trackingData } = useOrderDetails();
  const { userJourneyTracker } = useUserJourneys();

  const [applicationData, setApplicationData] = useState({});
  const [decisionStatus, setDecisionStatus] = useState(null);
  const [capitalShopClient, setCapitalShopClient] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showDecision, setShowDecision] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const formHook = useForm();

  const firstPending = useRef(true);

  const handleCapitalShopResponse = (resp) => {
    // update form with saved information from capital-shop, if any
    // this information only comes when the status is pending, so we can optimize here
    const incomingStatus = resp.data.attributes.status;
    if (incomingStatus === 'pending' && firstPending.current) {
      const formAttributes = Object.fromEntries(
        Object.entries(resp.data.attributes).filter(
          ([key]) =>
            key.startsWith('personal_') || key === 'accepted_legal_agreements',
        ),
      );
      if (Object.entries(formAttributes).length > 0) {
        setApplicationData(formAttributes);
      }
    }
    setDecisionStatus(incomingStatus);
  };

  // this is responsible for pre-loading the form with any saved values from capital-shop on first load
  useEffect(() => {
    // capitalShopClient starts off as null because it requires us to load the application flow token first, which
    // we will do here
    if (capitalShopClient === null) {
      orderRefresh().then((data) => {
        const applicationFlowToken =
          data.installment_summary.application_flow_token;

        // sanity check to see if this is an installment order
        if (applicationFlowToken === undefined) {
          pushHistoryWithSearchParams(history, '/payment');
          return;
        }
        setCapitalShopClient(new CapitalShopClient(applicationFlowToken));
      });
      return;
    }

    capitalShopClient.getApplicationFlow().then((resp) => {
      const status = resp.data.attributes.status;
      if (status === 'reviewed') {
        // we already got approved, send customer to payment
        pushHistoryWithSearchParams(history, '/payment');
        return;
      } else if (status === 'declined') {
        // this should never happen, move them back to beginning
        pushHistoryWithSearchParams(history, '/information');
        return;
      }
      // it's should be pending 99% of the time
      handleCapitalShopResponse(resp);
    });
  }, [capitalShopClient]);

  useEffect(() => {
    onCheckoutPageView('Financing: Application', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.FINANCING_APPLICATION,
      additionalParameters: trackingData,
    });
  }, []);

  const pollForResult = () => {
    setTimeout(() => {
      capitalShopClient
        .getApplicationFlow()
        .then((resp) => {
          // force "infinite loop" until a decision has been rendered
          if (resp.data.attributes.status === 'submitted') {
            pollForResult();
            return;
          }
          handleCapitalShopResponse(resp);
        })
        .catch(() => setDecisionStatus('pending'));
    }, 1500);
  };

  const handleDecisionStatusChange = () => {
    if (decisionStatus === null) {
      return;
    }

    // when we first submit, we have to start the polling process
    if (decisionStatus === 'submitted') {
      pollForResult();
      return;
    }

    // the initial state is always pending, so we only care if it goes BACK to this state
    if (decisionStatus === 'pending' && firstPending.current) {
      firstPending.current = false;
      return;
    }

    // if we pass all checks above, it means a decision has been rendered, so load the dialog
    // the decision is either: [pending, reviewed, declined]
    setSubmitted(false);
    setShowLoading(false);
    setShowDecision(true);
  };

  useEffect(handleDecisionStatusChange, [decisionStatus]);

  if (decisionStatus === null) {
    return <PageLoader />;
  }

  // ultra edge case: customer submits application, leaves the flow, and then comes back to finance_app while the application is still submitting
  // we can identify this by checking if the decisionStatus is submitted and firstPending is still true, as firstPending during "normal" flow should
  // have immediately been set to false
  // solution: make them see the spinner until a decision is rendered
  if (firstPending.current && decisionStatus === 'submitted') {
    firstPending.current = false;
    setSubmitted(true);
    setShowLoading(true);
  }

  const submitToCapitalShop = (data) => {
    const payload = {};
    Object.entries(data)
      .filter(
        ([key]) =>
          key.startsWith('personal_') || key === 'accepted_legal_agreements',
      )
      .forEach(([name, field]) => (payload[name] = field));
    capitalShopClient
      .submitApplicationFlow(payload)
      .then(handleCapitalShopResponse)
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.FinanceAppStepApplicationFlowSubmitError,
        });
        setSubmitted(false);
        setShowLoading(false);
        setShowDecision(true);
      });
  };

  const onSuccess = (data) => {
    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.financing_submitted,
      trackingData,
      componentName: 'Financing Application',
      actionItem: 'submit_application',
    });
    setSubmitted(true);
    setShowLoading(true);
    submitToCapitalShop(data);
  };

  const responseDialog = () => {
    switch (decisionStatus) {
      case 'reviewed': {
        return <Reviewed />;
      }
      case 'declined': {
        return <Declined />;
      }
      default: {
        // pending is the default because it does not let the user continue
        return <Pending onClose={() => setShowDecision(false)} />;
      }
    }
  };

  return (
    <>
      <h2>{t('checkout.financeApp.title')}</h2>
      <p style={{ margin: theme.spacing(1, 0) }}>
        {t('checkout.financeApp.body')}
      </p>
      <FormProvider {...formHook}>
        <ApplicationForm
          applicationData={applicationData}
          disabled={submitted}
          onSubmit={formHook.handleSubmit(onSuccess)}
        />
      </FormProvider>
      <LoadingDecision open={showLoading} />
      <Dialog fullWidth maxWidth="xs" open={showDecision}>
        {responseDialog()}
      </Dialog>
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default FinanceApp;
